import { useEffect, useId, useRef, useState, type ImgHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';

type LightboxImageProps = {
  src: string;
  alt: string;
  className?: string;
  imageClassName?: string;
  gallery?: Array<{ src: string; alt: string }>;
  galleryIndex?: number;
  priority?: boolean;
  width?: number;
  height?: number;
};

export function LightboxImage({
  src,
  alt,
  className = '',
  imageClassName = 'object-cover',
  gallery,
  galleryIndex = 0,
  priority = false,
  width,
  height,
}: LightboxImageProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(galleryIndex);
  const [isMagnified, setIsMagnified] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const instanceId = useId();
  const hasGallery = Boolean(gallery && gallery.length > 1);

  useEffect(() => {
    const handleOtherOpen = (e: Event) => {
      const { id } = (e as CustomEvent<{ id: string }>).detail;
      if (id !== instanceId) setOpen(false);
    };
    window.addEventListener('lightbox-open', handleOtherOpen);
    return () => window.removeEventListener('lightbox-open', handleOtherOpen);
  }, [instanceId]);
  const resolvedIndex = hasGallery ? ((activeIndex % gallery!.length) + gallery!.length) % gallery!.length : 0;
  const displaySrc = hasGallery ? gallery![resolvedIndex].src : src;
  const displayAlt = hasGallery ? gallery![resolvedIndex].alt : alt;

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
      if (event.key === 'ArrowLeft' && hasGallery) {
        setActiveIndex((prev) => prev - 1);
      }
      if (event.key === 'ArrowRight' && hasGallery) {
        setActiveIndex((prev) => prev + 1);
      }
    };

    const triggerElement = triggerRef.current;
    document.body.classList.add('lightbox-open');
    closeButtonRef.current?.focus();
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.classList.remove('lightbox-open');
      window.removeEventListener('keydown', onKeyDown);
      triggerElement?.focus();
    };
  }, [open, hasGallery]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          window.dispatchEvent(new CustomEvent('lightbox-open', { detail: { id: instanceId } }));
          if (gallery && gallery.length > 0) setActiveIndex(galleryIndex);
          setIsMagnified(false);
          setOpen(true);
        }}
        className={`lightbox-trigger relative overflow-hidden focus:outline-none ${className}`}
        aria-label={`Open image preview: ${alt}`}
      >
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          className={`w-full ${imageClassName}`}
          {...(priority
            ? // React 18 prints camelCase `fetchPriority` as-is. Lighthouse only accepts `fetchpriority`.
              ({ fetchpriority: 'high', loading: 'eager' } as ImgHTMLAttributes<HTMLImageElement>)
            : { loading: 'lazy' as const, decoding: 'async' as const })}
        />
      </button>
      {open && createPortal(
        <div
          className="lightbox-overlay fixed inset-0 z-50 overflow-x-auto overflow-y-auto bg-black/80 p-2 sm:p-6"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`Image preview: ${alt}`}
        >
          <div className="lightbox-controls sticky top-0 z-20 ml-auto flex w-max max-w-full items-center gap-2 pb-2">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setIsMagnified((prev) => !prev);
              }}
              className="tab-control lightbox-control-btn text-[0.55rem] tracking-[0.3em] px-3 py-1"
              aria-label={isMagnified ? 'Unzoom image' : 'Zoom image'}
              title={isMagnified ? 'Unzoom image' : 'Zoom image'}
            >
              {isMagnified ? 'UNZOOM' : 'ZOOM'}
            </button>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={() => setOpen(false)}
              className="tab-control lightbox-control-btn text-[0.55rem] tracking-[0.3em] px-3 py-1"
              aria-label="Close image preview"
            >
              CLOSE
            </button>
          </div>
          <div className="lightbox-stage mx-auto flex min-h-[calc(100dvh-3rem)] w-full items-center justify-center py-4 sm:py-8">
            <div className="relative" onClick={(event) => event.stopPropagation()}>
              <img
                src={displaySrc}
                alt={displayAlt}
                decoding="async"
                onClick={() => setIsMagnified((prev) => !prev)}
                className={`lightbox-image h-auto w-auto object-contain ${
                  isMagnified
                    ? 'max-h-none max-w-none cursor-zoom-out'
                    : 'max-h-[calc(100dvh-8rem)] max-w-[calc(100vw-1rem)] sm:max-h-[calc(100dvh-4rem)] sm:max-w-[calc(100vw-4rem)] cursor-zoom-in'
                }`}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
